import type {Blame, BlameCommit, BlameRange} from '@github-ui/code-view-types'
import {useCurrentRepository} from '@github-ui/current-repository'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {blamePath} from '@github-ui/paths'
import {Link} from '@github-ui/react-core/link'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
// eslint-disable-next-line no-restricted-imports
import {ScreenSize, useScreenSize} from '@github-ui/screen-size'
import {useBlameAgeColor} from '@github-ui/use-blame-age-color'
import {VersionsIcon} from '@primer/octicons-react'
import {RelativeTime} from '@primer/react'
import {Tooltip} from '@primer/react/next'
import {useWindowVirtualizer} from '@tanstack/react-virtual'
import {clsx} from 'clsx'
import React, {useCallback, useImperativeHandle, useLayoutEffect, useRef, useState} from 'react'

import {useCurrentBlame} from '../../../../hooks/CurrentBlame'
import {isLineInViewport} from '../../../../utilities/lines'
import styles from './Blame.module.css'
import type {CodeLinesHandle} from './code-lines-handle'
import {CodeLine} from './CodeLine'
import type {CodeLineData} from './hooks/use-code-lines'
import {LineNumber} from './LineNumber'

const codeRowHeightInPixels = 20
const singleBlameLineHeightWide = 31 // 20px line height + 10px vertical padding + 1px border
const singleBlameLineHeightNarrow = 41 // 20px line height + 20px vertical padding + 1px border
const defaultOverscanAmount = 100

// Constructed once at module scope. `BlameForRange` mounts once per visible
// virtualized row, so per-row construction of `Intl.DateTimeFormat` added up
// noticeably on large blames (the constructor is significantly more expensive
// than the `.format()` call itself, and ~2x slower on Safari than on V8).
const longDateFormatter = new Intl.DateTimeFormat(undefined, {year: 'numeric', month: 'short', day: 'numeric'})

interface BlameLinesProps {
  linesData: readonly CodeLineData[]
  colorizedLines?: SafeHTMLString[]
  tabSize: number
  materializeAllLines?: boolean
  copilotAccessAllowed: boolean
  onLineNumberClick?: React.MouseEventHandler<HTMLDivElement>
  ref?: React.Ref<CodeLinesHandle>
}

export function BlameLinesSSR({
  copilotAccessAllowed,
  linesData,
  colorizedLines,
  tabSize,
  onLineNumberClick,
}: BlameLinesProps) {
  const parentRef = useRef<HTMLTableElement>(null)

  const blame = useCurrentBlame()
  const blameSegments = extractBlameSegments(blame, linesData)
  return (
    <div className="d-flex flex-column" style={{tabSize}} ref={parentRef} data-hpc>
      {blameSegments.map(segment => (
        <BlameSegment
          key={`blame-for-segment-${segment.range?.start ?? segment.linesData[0]!.lineNumber}`}
          {...segment}
          colorizedLines={colorizedLines}
          copilotAccessAllowed={copilotAccessAllowed}
          onLineNumberClick={onLineNumberClick}
        />
      ))}
    </div>
  )
}

// eslint-disable-next-line @typescript-eslint/no-shadow -- named function expression intentionally shares the outer name
export const BlameLines = React.memo(function BlameLines({
  copilotAccessAllowed,
  linesData,
  colorizedLines,
  tabSize,
  materializeAllLines,
  onLineNumberClick,
  ref,
}: BlameLinesProps) {
  const parentRef = useRef<HTMLTableElement>(null)
  const blame = useCurrentBlame()
  const {screenSize} = useScreenSize()

  // Track parent offset for scrollMargin calculation
  const [parentOffset, setParentOffset] = useState(0)

  useLayoutEffect(() => {
    setParentOffset(parentRef.current?.offsetTop ?? 0)
  }, [])

  /**
   * This size estimation function is used by the virtualizer to guess which elements should appear in the window.
   * It doesn't need to be perfect - it just needs to get close.
   */
  const isWide = screenSize >= ScreenSize.medium

  const estimateSize = useCallback(
    (index: number) => {
      const line = linesData[index]!
      const range = blame?.ranges[line.lineNumber]
      if (range && range.end === range.start) {
        return isWide ? singleBlameLineHeightWide : singleBlameLineHeightNarrow
      }

      return codeRowHeightInPixels
    },
    [linesData, blame, isWide],
  )

  const virtualizer = useWindowVirtualizer({
    count: linesData.length,
    overscan: materializeAllLines ? Number.MAX_SAFE_INTEGER : defaultOverscanAmount,
    estimateSize,
    scrollMargin: parentOffset,
  })

  const virtualLinesData = virtualizer.getVirtualItems().map(virtualItem => ({
    ...linesData[virtualItem.index]!,
    virtualOffset: virtualItem.start - virtualizer.options.scrollMargin,
  }))

  useImperativeHandle(ref, () => ({
    scrollToTop: () => {
      if (isLineInViewport(0)) return
      virtualizer.scrollToIndex(0, {align: 'start'})
    },
    scrollToLine: lineNumber => {
      if (isLineInViewport(lineNumber)) return
      virtualizer.scrollToIndex(lineNumber, {align: 'center'})
    },
  }))

  const blameSegments = extractBlameSegments(blame, virtualLinesData)

  return (
    <div
      style={{
        '--blame-segments-count': blameSegments.length,
        '--blame-virt-total-size': `${virtualizer.getTotalSize()}px`,
        tabSize,
      }}
      className={styles.virtualBlameWrapper}
      ref={parentRef}
      data-hpc
    >
      {blameSegments.map((segment, index) => {
        let virtualOffset = segment.linesData[0]!.virtualOffset ?? 0

        if (screenSize < ScreenSize.large) {
          virtualOffset += (index ?? 0) * singleBlameLineHeightNarrow
        }

        return (
          <BlameSegment
            key={`blame-for-segment-${segment.range?.start ?? segment.linesData[0]!.lineNumber}`}
            range={segment.range}
            commit={segment.commit}
            linesData={segment.linesData}
            colorizedLines={colorizedLines}
            copilotAccessAllowed={copilotAccessAllowed}
            onLineNumberClick={onLineNumberClick}
            virtualOffset={virtualOffset}
          />
        )
      })}
    </div>
  )
})

// Pick only the data-model fields BlameSegment renders, keeping the prop contract (and the
// exhaustive comparator below) decoupled from the full extractBlameSegments shape. virtualOffset
// is a parent-computed layout prop, not segment data, so it's declared directly.
interface BlameSegmentProps extends Pick<BlameSegmentData, 'commit' | 'range' | 'linesData'> {
  colorizedLines?: SafeHTMLString[]
  copilotAccessAllowed: boolean
  onLineNumberClick?: React.MouseEventHandler<HTMLDivElement>
  virtualOffset?: number
}

function BlameSegmentContent({
  range,
  commit,
  linesData,
  colorizedLines,
  copilotAccessAllowed,
  onLineNumberClick,
}: Omit<BlameSegmentProps, 'virtualOffset'>) {
  return (
    <>
      {range && commit ? <BlameForRange range={range} commit={commit} /> : <div className="height-full" />}
      <div className={clsx(styles.lineCodePairs, 'd-flex flex-column')}>
        {linesData.map(lineData => (
          <div key={`line-pair-${lineData.lineNumber}`} className="d-flex flex-row">
            {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
            <div className={clsx('react-line-numbers', linesData.length === 1 && styles.blameNoLineData)}>
              <LineNumber
                key={`line-number-${lineData.lineNumber}-content-${lineData.rawText?.substring(0, 100)}`}
                codeLineData={lineData}
                onClick={onLineNumberClick}
              />
            </div>
            <div className={clsx(styles.codeLine, linesData.length === 1 && styles.blameNoLineData)}>
              <CodeLine
                stylingDirectivesLine={lineData.stylingDirectivesLine}
                key={`code-line=${lineData.lineNumber}-content-${lineData.rawText?.substring(0, 100)}`}
                id={`LC${lineData.lineNumber}`}
                codeLineData={lineData}
                copilotAccessAllowed={copilotAccessAllowed}
                shouldUseInert={false}
                colorizedLine={colorizedLines?.[lineData.lineNumber - 1]}
              />
            </div>
          </div>
        ))}
      </div>
    </>
  )
}

/**
 * Shallow equality for a single virtualized line.
 *
 * `linesData` rows are rebuilt every render as `{...stableCodeLineData, virtualOffset}`, so they
 * never compare `===`. But their object-valued fields (`stylingDirectivesLine`, `ownedSection`,
 * `codeownersLineError`) are stable references from the memoized `useCodeLines` source, and every
 * other field is a primitive. A shallow per-field compare is therefore equivalent to a deep equal
 * for these inputs while skipping the expensive recursion into each line's `stylingDirectivesLine`
 * array. It is also strictly safe: equal field references imply a deep-equal object, so a shallow
 * match never skips a render that a deep compare would have caught.
 */
function areLineDataShallowEqual(a: VirtualCodeLineData, b: VirtualCodeLineData): boolean {
  if (a === b) return true
  const aKeys = Object.keys(a) as Array<keyof VirtualCodeLineData>
  if (aKeys.length !== Object.keys(b).length) return false
  for (const key of aKeys) {
    if (!Object.is(a[key], b[key])) return false
  }
  return true
}

function areLinesDataEqual(a: readonly VirtualCodeLineData[], b: readonly VirtualCodeLineData[]): boolean {
  if (a === b) return true
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    if (!areLineDataShallowEqual(a[i]!, b[i]!)) return false
  }
  return true
}

/**
 * Per-prop comparison strategy for {@link BlameSegment}'s `React.memo`.
 *
 * Declared as an exhaustive map over every prop in `BlameSegmentProps` — the `-?`
 * mapped-type modifier makes all keys required, so adding a new prop becomes a compile
 * error until its comparison strategy is declared here. This closes the common
 * custom-`memo` footgun where a newly added prop silently fails to trigger re-renders.
 *
 * Key order matters: cheap reference/value checks run first and the per-line `linesData`
 * comparison runs last, so a difference in any cheaper prop short-circuits before it.
 */
const blameSegmentPropsComparators: {
  [Key in keyof BlameSegmentProps]-?: (a: BlameSegmentProps[Key], b: BlameSegmentProps[Key]) => boolean
} = {
  range: Object.is,
  commit: Object.is,
  virtualOffset: Object.is,
  copilotAccessAllowed: Object.is,
  onLineNumberClick: Object.is,
  // colorizedLines is a stable reference from context, so reference equality avoids an O(N) deep comparison
  colorizedLines: Object.is,
  // linesData rows are rebuilt each render but only shallow-differ (stable nested refs), so a per-line
  // shallow compare is equivalent to deep equality here and avoids recursing into stylingDirectivesLine
  linesData: areLinesDataEqual,
}

const blameSegmentComparatorKeys = Object.keys(blameSegmentPropsComparators) as Array<keyof BlameSegmentProps>

/**
 * `React.memo` comparator for {@link BlameSegment}: returns `true` when the segment can skip
 * re-rendering. Exhaustive over {@link blameSegmentPropsComparators}. Exported for unit testing.
 */
export function areBlameSegmentPropsEqual(prevProps: BlameSegmentProps, nextProps: BlameSegmentProps): boolean {
  for (const key of blameSegmentComparatorKeys) {
    const areEqual = blameSegmentPropsComparators[key] as (a: unknown, b: unknown) => boolean
    if (!areEqual(prevProps[key], nextProps[key])) {
      return false
    }
  }
  return true
}

const BlameSegment = React.memo(
  // eslint-disable-next-line @typescript-eslint/no-shadow -- named function expression intentionally shares the outer name
  function BlameSegment({
    range,
    commit,
    linesData,
    colorizedLines,
    virtualOffset,
    copilotAccessAllowed,
    onLineNumberClick,
  }: BlameSegmentProps) {
    return (
      <div
        className={styles.blameSegmentWrapper}
        style={
          virtualOffset !== undefined
            ? {
                position: 'absolute',
                top: `${virtualOffset}px`,
              }
            : undefined
        }
      >
        <BlameSegmentContent
          range={range}
          commit={commit}
          linesData={linesData}
          copilotAccessAllowed={copilotAccessAllowed}
          colorizedLines={colorizedLines}
          onLineNumberClick={onLineNumberClick}
        />
      </div>
    )
  },
  areBlameSegmentPropsEqual,
)

interface VirtualLineData {
  virtualOffset?: number
}

interface VirtualCodeLineData extends CodeLineData, VirtualLineData {}

interface BlameSegmentData {
  commit?: BlameCommit
  range?: BlameRange
  linesData: readonly VirtualCodeLineData[]
}

interface InProgressBlameSegmentData extends BlameSegmentData {
  linesData: VirtualCodeLineData[]
}

function extractBlameSegments(blame: Blame | undefined, linesData: readonly VirtualCodeLineData[]): BlameSegmentData[] {
  if (!blame) return [{linesData}]

  const segments: InProgressBlameSegmentData[] = []

  let current: InProgressBlameSegmentData | null = null
  let isFirst = true
  for (const lineData of linesData) {
    if (!current) {
      current = {linesData: []}
    }

    const range = isFirst
      ? Object.values(blame?.ranges ?? {}).find(r => r.start <= lineData.lineNumber && r.end >= lineData.lineNumber)
      : blame?.ranges[lineData.lineNumber]
    if (range) {
      current.range = range
      current.commit = blame.commits[range.commitOid]
    }

    current.linesData.push(lineData)

    if (current.range?.end === lineData.lineNumber) {
      segments.push(current)
      current = null
    }

    isFirst = false
  }

  if (current) {
    segments.push(current)
    current = null
  }

  return segments
}

const BlameForRange = React.memo(BlameForRangeUnmemoized)

function BlameForRangeUnmemoized({range, commit}: {range: BlameRange; commit: BlameCommit}) {
  const repo = useCurrentRepository()
  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const commitDate = new Date(commit.committedDate)
  const repoCreationDate = new Date(repo.createdAt)

  const timestampAgoElement = (
    <div className={styles.timestampAgo}>
      <RelativeTime date={commitDate} tense="past" className={styles.RelativeTime} />
    </div>
  )

  return (
    <div className={clsx(styles.blameForRange, 'd-flex')}>
      <div aria-hidden className={styles.ageIndicator}>
        <BlameAgeIndicator commitDate={commitDate} repoCreationDate={repoCreationDate} />
      </div>
      <div className={clsx('pt-1', styles.timestampWrapperDesktop)}>{timestampAgoElement}</div>
      <div className={styles.authorAvatarWrapper}>
        {commit.authorAvatarUrl && <GitHubAvatar src={commit.authorAvatarUrl} size={18} />}
      </div>
      <div className={styles.commitMessageWrapper}>
        <div className="d-flex">
          <VerifiedHTMLText
            html={commit.shortMessageHtmlLink}
            className={styles.commitMessage}
            {...getHovercardAttributesForType('commit', {
              owner: repo.ownerLogin,
              repo: repo.name,
              commitish: commit.oid,
            })}
          />
        </div>
      </div>
      <div className={styles.reblameWrapper}>
        <div className={clsx('pt-1 tmp-pr-3', styles.timestampWrapperMobile)}>{timestampAgoElement}</div>
        <ReblameButton range={range} commit={commit} commitDate={commitDate} />
      </div>
    </div>
  )
}

function ReblameButton({range, commit, commitDate}: {range: BlameRange; commit: BlameCommit; commitDate: Date}) {
  const repo = useCurrentRepository()
  if (!range.reblamePath) return null

  const href = blamePath({
    owner: repo.ownerLogin,
    repo: repo.name,
    commitish: commit.firstParentOid,
    filePath: range.reblamePath,
  })

  const shortSHA = commit.oid.slice(0, 7)
  const formattedDate = longDateFormatter.format(commitDate)
  const blameLabel = `Blame prior to change ${shortSHA}, made on ${formattedDate}`
  const reblameId = `reblame-${shortSHA}`

  return (
    <Tooltip text={blameLabel} type="label" id={reblameId}>
      <Link aria-labelledby={reblameId} to={href} className="Button Button--iconOnly Button--invisible Button--small">
        <VersionsIcon />
      </Link>
    </Tooltip>
  )
}

function BlameAgeIndicator({commitDate, repoCreationDate}: {commitDate: Date; repoCreationDate: Date}) {
  const color = useBlameAgeColor(commitDate, repoCreationDate)
  return <div className={styles.blameAgeIndicator} style={{backgroundColor: color}} />
}
